import { useEffect, useMemo } from "react";

import { TierModel } from "@doitintl/cmp-models";
import { getCollection } from "@doitintl/models-any";
import { useCollectionDataOnce } from "@doitintl/models-hooks";
import { MenuItem, TextField } from "@mui/material";

import { contractTypeTiers, packageTypeMap } from "../../../const";
import { stateDefaultValues, useContractFormContext } from "../../../ContractsFormContext";
import { type NavigatorSolveContractState } from "../../../types";
import { calculateChargePerTerm, isMonthlyFlatRateApplicable, isNavigatorSolve } from "../../../utils";

const tiersOrder = ["standard", "enhanced", "premium", "enterprise"];

export const TierSelector = () => {
  const { state, setState } = useContractFormContext();

  const [tiers] = useCollectionDataOnce(
    getCollection(TierModel)
      .where("packageType", "==", packageTypeMap[state.type])
      .where("name", "in", contractTypeTiers[state.type]),
    {
      idField: "id",
      refField: "ref",
    }
  );

  const sortedTiers = useMemo(
    () =>
      (tiers ?? [])
        .filter((x) => !x.displayName.toLowerCase().includes("dci"))
        .sort((a, b) => tiersOrder.indexOf(a.name) - tiersOrder.indexOf(b.name)),
    [tiers]
  );

  useEffect(() => {
    if (!isNavigatorSolve(state)) {
      return;
    }

    if (sortedTiers.length && state.tierId && !state.tier) {
      const tier = sortedTiers.find((t) => t.id === state.tierId);
      setState((prevState) => ({ ...prevState, tier }));
    }
  }, [sortedTiers, setState, state]);

  const handleChange = ({ target: { value } }) => {
    if (!isNavigatorSolve(state)) {
      return;
    }

    const tierId = value;
    const tierDetails = sortedTiers?.find((t) => t.id === tierId);
    const isEnterprise = tierDetails?.name === "enterprise";
    const tierPrice = isEnterprise ? 0 : tierDetails?.price?.[state.currency];
    const chargePerTerm = calculateChargePerTerm(state.paymentTerm, state.commitmentMonths, tierPrice);
    const updatedMonthlyFlatRate = isMonthlyFlatRateApplicable(tierDetails)
      ? isEnterprise
        ? state.monthlyFlatRate === undefined ||
          state.monthlyFlatRate === (stateDefaultValues as NavigatorSolveContractState).monthlyFlatRate
          ? 0 // Set to 0 if state.monthlyFlatRate is the default value
          : state.monthlyFlatRate // Retain the updated value
        : state.monthlyFlatRate || (stateDefaultValues as NavigatorSolveContractState).monthlyFlatRate // For non-enterprise tiers
      : undefined;
    const errors = { chargePerTerm: false, monthlyFlatRate: false, tierId: false };

    setState((prevState) => ({
      ...prevState,
      tierId,
      tier: tierDetails,
      chargePerTerm,
      monthlyFlatRate: updatedMonthlyFlatRate,
      errors: {
        ...prevState.errors,
        ...errors,
      },
    }));
  };

  if (!isNavigatorSolve(state)) {
    return null;
  }

  return (
    <TextField
      label="Tier"
      data-cy="tier"
      value={state.tierId}
      onChange={handleChange}
      error={state.errors.tierId}
      disabled={state.isEditForbidden}
      select
      fullWidth
      margin="normal"
      variant="outlined"
    >
      {sortedTiers?.map((tier) => (
        <MenuItem key={tier.id} value={tier.id}>
          {tier.displayName}
        </MenuItem>
      ))}
    </TextField>
  );
};
